import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { LifeBuoy, Send, Loader2, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  bug: 'Problem / Bug',
  question: 'Question',
  feature: 'Feature Request',
  feedback: 'General Feedback',
};

const STATUS_STYLES = {
  open: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400',
  in_progress: 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400',
  resolved: 'bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400',
};

const STATUS_LABELS = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved' };

export default function HelpSupport() {
  const { token } = useAuth();
  const { toast } = useToast();
  const headers = { Authorization: `Bearer ${token}` };

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({ category: 'question', subject: '', message: '' });

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API}/support/requests`, { headers });
      setRequests(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchRequests(); }, [token]);

  const handleSubmit = async () => {
    if (!form.subject.trim() || !form.message.trim()) {
      toast({ title: 'Subject and message are required', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/support/requests`, form, { headers });
      toast({ title: 'Request submitted — we\'ll get back to you soon!' });
      setForm({ category: 'question', subject: '', message: '' });
      fetchRequests();
    } catch (err) {
      toast({ title: 'Failed to submit', description: err.response?.data?.detail, variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="help-support-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center">
          <LifeBuoy className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Help &amp; Support</h1>
          <p className="text-sm text-gray-500">Ask a question, report a problem, or share feedback — we read everything.</p>
        </div>
      </div>

      <Card className="bg-white dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.08]">
        <CardHeader>
          <CardTitle className="text-base text-gray-900 dark:text-white">Send us a message</CardTitle>
          <CardDescription>We typically respond within one business day.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-700 dark:text-gray-300">Category</Label>
              <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                <SelectTrigger data-testid="support-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-gray-700 dark:text-gray-300">Subject</Label>
              <Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                placeholder="Brief summary of your request" data-testid="support-subject-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-700 dark:text-gray-300">Message</Label>
            <Textarea rows={5} value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
              placeholder="Tell us what's going on — the more detail the better" data-testid="support-message-input" />
          </div>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="support-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Submit Request
          </Button>
        </CardContent>
      </Card>

      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Your Requests</h2>
        {loading ? (
          <p className="text-sm text-gray-400 py-6 text-center">Loading...</p>
        ) : requests.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center" data-testid="no-requests-msg">No requests yet — anything we can help with?</p>
        ) : (
          <div className="space-y-3">
            {requests.map(r => (
              <div key={r.id} className="bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] rounded-lg" data-testid={`support-request-${r.id}`}>
                <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="w-full flex items-center gap-3 p-4 text-left">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{r.subject}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{CATEGORY_LABELS[r.category] || r.category} &bull; {new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  {r.replies?.length > 0 && (
                    <span className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400"><MessageSquare className="w-3.5 h-3.5" /> {r.replies.length}</span>
                  )}
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[r.status] || STATUS_STYLES.open}`} data-testid={`request-status-${r.id}`}>
                    {STATUS_LABELS[r.status] || r.status}
                  </span>
                  {expanded === r.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </button>
                {expanded === r.id && (
                  <div className="px-4 pb-4 space-y-3 border-t border-gray-100 dark:border-white/5 pt-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{r.message}</p>
                    {(r.replies || []).map(rep => (
                      <div key={rep.id} className="bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20 rounded-lg p-3">
                        <p className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 mb-1">Miadesk Support &bull; {new Date(rep.created_at).toLocaleString()}</p>
                        <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{rep.message}</p>
                      </div>
                    ))}
                    {(!r.replies || r.replies.length === 0) && r.status !== 'resolved' && (
                      <p className="text-xs text-gray-400 italic">Waiting for a reply from our team...</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
